<template>
    <div
        class="device-controls__block"
        :class="{
            'dci__order-2': evcsAction != null,
            'dci__order-1': evcsAction != null && evcsInstance == instance,
        }"
    >
        <div class="device-controls__row device-controls__row--title">
            <span class="dci__title">
                <span class="sg-vrm-icon_108_ev-charger-16px"></span>
                {{ formattedName }}
            </span>
            <div v-if="isWaitingForResponse" class="dci__status">
                <spinner />
                {{ $t('dashboard.dci.loading') }}
            </div>
            <div v-else-if="startStop" class="dci__status">
                <spinner />
                {{ $t('dashboard.multi.state_260') }}
            </div>
            <div v-else class="dci__status">
                {{ $t('dashboard.dci.stopped') }}
            </div>
        </div>
        <div
            v-for="(timer, index) in [setModeTimer, setChargeTimer, changeLimitTimer]"
            :key="index"
        >
            <div
                v-if="timer.nextChange !== null"
                :style="{ height: bodyHeight + 'px' }"
                class="device-controls__body device-controls__body--evcs"
            >
                <change-warning @cancel="timer.clearChangeTimer()" @confirm="timer.confirmChange()">
                    <div v-html="getTimerMessage(timer)"></div>
                </change-warning>
            </div>
        </div>
        <div
            v-if="
                setModeTimer.nextChange === null &&
                    setChargeTimer.nextChange === null &&
                    changeLimitTimer.nextChange === null
            "
            ref="evcsBody"
            class="device-controls__body device-controls__body--evcs"
        >
            <div class="device-controls__row">
                <h4
                    class="dci__subtitle"
                    :class="{ 'dci__subtitle--disabled': isWaitingForResponse }"
                >
                    {{ $t(`dashboard.dci.mode`, { i18nFallback: 'Mode' }) }}
                </h4>
                <span v-if="modeChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + modeChange.error) }}
                </span>
            </div>
            <div
                v-for="option in radioValues"
                :key="option.valueEnum"
                class="device-controls__row device-controls__row--checkbox"
                :class="{ 'device-controls__row--disabled': isWaitingForResponse }"
                @click="
                    () => {
                        if (!isWaitingForResponse && mode !== option.valueEnum)
                            setModeWithTimer(option.valueEnum);
                    }
                "
            >
                <span class="dci__action">
                    {{
                        $t(`dashboard.dci.evcs.${option.nameEnum}`, {
                            i18nFallback: option.nameEnum,
                        })
                    }}
                </span>
                <div class="vrm-form__custom-radio-checkbox">
                    <div
                        class="sg-custom-check-and-radio custom-radio"
                        :class="{ 'sg-disabled': isWaitingForResponse }"
                    >
                        <label>
                            <input
                                type="radio"
                                :checked="option.valueEnum == mode"
                                :disabled="isWaitingForResponse"
                            />
                            <div class="sg-alternative-display">
                                <div class="sg-outer">
                                    <div class="sg-inner"></div>
                                </div>
                            </div>
                        </label>
                    </div>
                </div>
            </div>
            <div class="dci__border" />
            <div class="device-controls__row">
                <span
                    class="dci__action"
                    :class="{ 'dci__action--disabled': !isManual || isWaitingForResponse }"
                >
                    {{
                        $t(`dashboard.dci.evcs.chargeCurrent`, {
                            i18nFallback: 'Charge current',
                        })
                    }}
                </span>
                <input
                    type="number"
                    min="0"
                    class="sg-element"
                    :disabled="!isManual || isWaitingForResponse"
                    :value="current"
                    :error="currentChange.error"
                    @input="event => debounceChangeLimit(event)"
                />
                <span class="unit">A</span>
                <span v-if="currentChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + currentChange.error) }}
                </span>
            </div>

            <div class="device-controls__row">
                <button
                    v-for="amperage in AMPERAGES"
                    :key="amperage"
                    class="dci__inline-btn"
                    :class="{
                        'dci__inline-btn--active': amperage === +current,
                    }"
                    :disabled="!isManual || isWaitingForResponse"
                    @click="changeLimitWithTimer(amperage)"
                >
                    {{ amperage }}
                </button>
            </div>
            <div class="dci__border" />
            <div class="device-controls__row device-controls__row--switches">
                <span
                    class="dci__action"
                    :class="{ 'dci__action--disabled': isWaitingForResponse }"
                >
                    {{
                        $t(`dashboard.dci.evcs.charge`, {
                            i18nFallback: 'Charge',
                        })
                    }}
                </span>

                <vrm-controlled-toggle
                    :value="startStop"
                    :disabled="isWaitingForResponse"
                    @input="toggleStartStopWithTimer"
                />

                <span v-if="startStopChange.error" class="dci__warning dci__warning--inline">
                    {{ $t('dashboard.dci.errors.' + startStopChange.error) }}
                </span>
            </div>
        </div>
    </div>
</template>
<script>
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AngularLocaleBundle } from '@/angular-bridge/modules/i18n';
import { ngInject } from '@/vue/decorators/angular-interop';
import { createTimer } from './components/Timer';
import Spinner from './components/Spinner';
import ChangeWarning from '@/vue/components/shared/device-controls/controls/components/ChangeWarning';

import VrmControlledToggle from '@/vue/components/shared/toggles/VrmControlledToggle';

import { newMqttWriteValue, initiateValueLoading, resolveValue } from './writeValueUtils';

import {
    EVCSAmperage,
    getChargingStationSubscriptions,
    subscribeToInfo,
    unsubscribeFromInfo,
    EVCS_MODES,
    reloadIosEvcsWidgets,
    publishToPath,
} from './utils';

@Component({
    components: {
        VrmControlledToggle,
        Spinner,
        ChangeWarning,
    },
})
@AngularLocaleBundle('dashboard')
@AngularLocaleBundle('common')
export default class ChargingStation extends Vue {
    @ngInject()
    MQTTRealtimeService;
    @ngInject()
    $location;
    @ngInject()
    vrmNotificationsService;
    @ngInject()
    ApiService;

    @Prop({ type: Number, required: true })
    instance;
    @Prop({ type: Number, required: true })
    installationId;
    @Prop({ type: String, required: true })
    identifier;
    @Prop({ type: Number, required: true })
    timerLimit;

    radioValues = [
        { nameEnum: 'auto', valueEnum: 1 },
        { nameEnum: 'scheduled', valueEnum: 2 },
        { nameEnum: 'manual', valueEnum: 0 },
    ];

    SUBSCRIPTIONS = getChargingStationSubscriptions();
    AMPERAGES = EVCSAmperage;
    debouncedChangeLimit = null;

    // controlled by mqtt
    customName = null;
    current = null;
    mode = null;
    startStop = null;

    // inherited from createTimer at mount
    setModeTimer = {};
    setChargeTimer = {};
    changeLimitTimer = {};

    currentChange = newMqttWriteValue({ varName: 'current' });
    modeChange = newMqttWriteValue({ varName: 'mode' });
    startStopChange = newMqttWriteValue({ varName: 'startStop' });

    bodyHeight = 380;

    mounted() {
        this.subscribeToInfo = subscribeToInfo.bind(this);
        this.unsubscribeFromInfo = unsubscribeFromInfo.bind(this);
        this.publishToPath = publishToPath.bind(this);
        this.initiateValueLoading = initiateValueLoading.bind(this);
        this.resolveValue = resolveValue.bind(this);
        this.subscribeToInfo();
        createTimer.call(this, 'setModeTimer', this.timerLimit);
        createTimer.call(this, 'setChargeTimer', this.timerLimit);
        createTimer.call(this, 'changeLimitTimer', this.timerLimit);
    }

    beforeDestroy() {
        window.clearTimeout(this.currentChange.timer);
        window.clearTimeout(this.modeChange.timer);
        window.clearTimeout(this.startStopChange.timer);
        this.unsubscribeFromInfo();
    }

    @Watch('startStop')
    onStartStopChanged() {
        if (this.setChargeTimer.changeSeconds) {
            this.setChargeTimer.clearChangeTimer();
        }
        reloadIosEvcsWidgets();

        this.resolveValue(this.startStopChange, +this.startStop);
    }

    @Watch('mode')
    onModeChanged() {
        if (this.setModeTimer.changeSeconds) {
            this.setModeTimer.clearChangeTimer();
        }
        reloadIosEvcsWidgets();

        this.resolveValue(this.modeChange, this.mode);
    }

    @Watch('current')
    onCurrent() {
        this.resolveValue(this.currentChange, this.current);
    }

    get isWaitingForResponse() {
        return this.startStopChange.busy || this.modeChange.busy || this.currentChange.busy;
    }

    debounceChangeLimit(event) {
        clearTimeout(this.debouncedChangeLimit);

        this.debouncedChangeLimit = setTimeout(() => {
            this.changeLimitWithTimer(event.target.value);
        }, 1500);
    }

    changeLimitWithTimer(value) {
        if (this.isWaitingForResponse || +value === this.current) return;
        this.matchBodyHeight();
        this.changeLimitTimer.callFuncWithTimer(value, this.changeLimit);
    }

    changeLimit(value) {
        this.currentChange.oldState = this.current;
        this.currentChange.newState = +value;
        this.initiateValueLoading(this.currentChange);

        this.publishToPath(
            {
                dbusPath: '/SetCurrent',
                callbackIdentifier: `setEvcsLimit${this.instance}`,
            },
            { value: +value },
            this.current,
        );
    }

    setModeWithTimer(newValue) {
        if (this.isWaitingForResponse) return;
        this.matchBodyHeight();
        this.setModeTimer.callFuncWithTimer(newValue, this.setMode);
    }

    setMode(value) {
        this.modeChange.oldState = this.mode;
        this.modeChange.newState = value;
        this.initiateValueLoading(this.modeChange);

        this.publishToPath(
            {
                dbusPath: '/Mode',
                callbackIdentifier: `setEvcsMode${this.instance}`,
            },
            { value },
            this.mode,
        );
    }

    toggleStartStopWithTimer(newValue) {
        if (this.isWaitingForResponse) return;
        this.matchBodyHeight();
        this.setChargeTimer.callFuncWithTimer(newValue, this.toggleStartStop);
    }

    toggleStartStop(value) {
        this.startStopChange.oldState = +this.startStop;
        this.startStopChange.newState = +value;
        this.initiateValueLoading(this.startStopChange);

        this.publishToPath(
            {
                dbusPath: '/StartStop',
                callbackIdentifier: `setEvcsStartStop${this.instance}`,
            },
            { value: +value },
            +this.startStop,
        );
    }

    get locationSearch() {
        return this.$location.search();
    }

    get isManual() {
        return this.mode == EVCS_MODES.MANUAL;
    }

    get formattedName() {
        if (this.customName) {
            return this.customName;
        }
        return this.$t('dashboard.dci.evcs.title', { instanceId: this.instance });
    }

    getCallbackIdentifier(varName) {
        return `get_${varName}ofChargingStation${this.instance}`;
    }

    get mqttPathObj() {
        return {
            identifier: this.identifier,
            dbusServiceType: 'evcharger',
            instance: this.instance,
        };
    }

    // locationSearch is not reactive so these getters will return the first observed value
    get evcsAction() {
        return this.locationSearch.evcsAction;
    }

    get evcsInstance() {
        return this.locationSearch.evcsInstance;
    }

    get setModeNotificationMsg() {
        const seconds = this.timerLimit - this.setModeTimer.changeSeconds;
        if (this.setModeTimer.nextChange === EVCS_MODES.SCHEDULED) {
            return this.$t('dashboard.dci.evcs.change_mode_scheduled', { seconds });
        } else if (this.setModeTimer.nextChange === EVCS_MODES.AUTO) {
            return this.$t('dashboard.dci.evcs.change_mode_auto', { seconds });
        }
        return this.$t('dashboard.dci.evcs.change_mode_manual', { seconds });
    }

    get setChargeNotificationMsg() {
        const seconds = this.timerLimit - this.setChargeTimer.changeSeconds;
        if (!this.startStop) {
            return this.$t('dashboard.dci.evcs.charge_start', { seconds });
        }
        return this.$t('dashboard.dci.evcs.charge_stop', { seconds });
    }

    get changeLimitNotificationMsg() {
        const seconds = this.timerLimit - this.changeLimitTimer.changeSeconds;
        return this.$t('dashboard.dci.countdown_timer_current', {
            nextState: this.changeLimitTimer.nextChange,
            seconds: seconds,
        });
    }

    getTimerMessage(timer) {
        if (timer === this.setModeTimer) {
            return this.setModeNotificationMsg;
        }
        if (timer === this.setChargeTimer) {
            return this.setChargeNotificationMsg;
        }
        if (timer === this.changeLimitTimer) {
            return this.changeLimitNotificationMsg;
        }
    }

    get bundleForQueryCommands() {
        return {
            location: this.$location,
            startStop: this.startStop,
            mode: this.mode,
        };
    }

    get computedNext() {
        const order = {
            [EVCS_MODES.AUTO]: EVCS_MODES.SCHEDULED,
            [EVCS_MODES.SCHEDULED]: EVCS_MODES.MANUAL,
            [EVCS_MODES.MANUAL]: EVCS_MODES.AUTO,
        };
        return order[+this.mode] ? order[+this.mode] : EVCS_MODES.AUTO;
    }

    @Watch('bundleForQueryCommands', { immediate: true })
    onQueryChange() {
        if (this.startStop === null || this.mode === null) {
            return;
        }
        this.$nextTick(() => {
            const evcsAction = this.$location.search().evcsAction;
            const evcsInstance = this.$location.search().evcsInstance;

            if (evcsAction == undefined || evcsInstance != this.instance) {
                return;
            }
            if (evcsAction == 'next_mode') {
                this.setModeWithTimer(this.computedNext);
            }
            if (evcsAction == 'toggle_charge') {
                this.toggleStartStopWithTimer(!this.startStop);
            }

            this.$location.search('evcsAction', null);
            this.$location.search('evcsInstance', null);
            this.$location.search('mobileOpenControls', null);
        });
    }

    matchBodyHeight() {
        if (this.$refs.evcsBody !== undefined) this.bodyHeight = this.$refs.evcsBody.clientHeight;
    }
}
</script>
